import React, { useState, useMemo, useCallback } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    HStack,
    Icon,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    AiOutlineSearch,
    AiOutlineGlobal,
    AiOutlineMobile,
    AiOutlineDesktop,
    AiOutlineAppstore,
    AiOutlineShop,
    AiOutlineUser,
    AiOutlineEnvironment,
    AiOutlineCheckCircle,
    AiOutlineCloseCircle,
    AiOutlineInfoCircle,
    AiOutlinePhone,
    AiOutlineFileSearch,
    AiOutlineSync,
} from 'react-icons/ai';
import { useNogleTech } from '../../../contexts/NogleTechContext';
import EmManutencao from '../../../components/UI/EmManutencao';

const PRODUTOS_NOGLE = [
    { key: 'site',             label: 'Site',             icon: AiOutlineGlobal,   campo: 'site_feito_contel' },
    { key: 'app_google',       label: 'App Google',       icon: AiOutlineMobile,   campo: 'app_google_feito_contel' },
    { key: 'app_apple',        label: 'App Apple',        icon: AiOutlineMobile,   campo: 'app_apple_feito_contel' },
    { key: 'painel',           label: 'Painel',           icon: AiOutlineDesktop,  campo: 'painel_feito_contel' },
    { key: 'venda_direta',     label: 'Venda Direta',     icon: AiOutlineShop,     campo: 'venda_direta_feito_contel' },
    { key: 'aplicativo',       label: 'App Próprio',      icon: AiOutlineAppstore, campo: 'status_possui_aplicativo' },
];

const InfoItem = ({ icon, label, value, valueColor }) => (
    <Box
        bg="surfaceRaised"
        border="1px solid"
        borderColor="borderPrimary"
        borderRadius="lg"
        px={4}
        py={3}
        _hover={{ borderColor: 'textMuted' }}
        transition="all 0.15s"
    >
        <HStack spacing={2} mb={1}>
            <Icon as={icon} color="textMuted" boxSize="12px" />
            <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1px">
                {label}
            </Text>
        </HStack>
        <Text fontSize="sm" color={valueColor || 'textPrimary'} fontWeight="700" lineHeight="1.4" noOfLines={2}>
            {value || '—'}
        </Text>
    </Box>
);

const ConsultaMarca = () => {
    const { marcas, toBool, loading, refetchMarcas } = useNogleTech();
    const [busca, setBusca] = useState('');
    const [marcaSelecionada, setMarcaSelecionada] = useState(null);

    const filtradas = useMemo(() => {
        if (!busca.trim()) return [];
        const q = busca.toLowerCase();
        return marcas.filter(m =>
            m.nome?.toLowerCase().includes(q) ||
            m.provedor?.toLowerCase().includes(q)
        ).slice(0, 8);
    }, [marcas, busca]);

    const handleSelecionar = useCallback((marca) => {
        setMarcaSelecionada(marca);
        setBusca(marca.nome || '');
    }, []);

    const countProdutos = (marca) => {
        if (!marca) return 0;
        return PRODUTOS_NOGLE.filter(p => toBool(marca[p.campo])).length;
    };

    // ⚠️ Tela em produção (pedido do Lucas em 16/07/2026): a Consulta de Marca
    // ainda não é usada da forma correta — exibe o aviso padrão "Em breve".
    // Para reativar a tela original, basta voltar esta flag para false
    // (o código completo permanece logo abaixo).
    const CONSULTA_MARCA_EM_BREVE = true;
    if (CONSULTA_MARCA_EM_BREVE) {
        return (
            <EmManutencao
                titulo="Consulta de Marca"
                descricao="A pesquisa de marcas com status de produtos e ficha do ERP está em produção e será liberada em breve."
            />
        );
    }

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header */}
            <Box>
                <Heading size="lg" color="textPrimary" fontWeight="900">
                    <HStack spacing={3}>
                        <Icon as={AiOutlineFileSearch} color="secondary" />
                        <Text>Consulta de Marca</Text>
                    </HStack>
                </Heading>
            </Box>

            {/* Search */}
            <HStack spacing={3} w="full">
                <Box position="relative" flex={1}>
                    <InputGroup size="lg">
                        <InputLeftElement pointerEvents="none" h="full">
                            <Icon as={AiOutlineSearch} color="textMuted" boxSize="20px" />
                        </InputLeftElement>
                        <Input
                            placeholder={loading ? 'Carregando marcas...' : `Buscar entre ${marcas.length} marcas...`}
                            value={busca}
                            onChange={e => { setBusca(e.target.value); if (!e.target.value) setMarcaSelecionada(null); }}
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: '0 0 0 1px #02EA75' }}
                            color="textPrimary"
                            borderRadius="xl"
                            fontSize="md"
                            fontWeight="600"
                        />
                    </InputGroup>

                    {/* Dropdown */}
                    {filtradas.length > 0 && !marcaSelecionada && (
                        <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            mt={1}
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            borderRadius="xl"
                            overflow="hidden"
                            zIndex={20}
                            boxShadow="0 8px 32px rgba(0,0,0,0.5)"
                            maxH="350px"
                            overflowY="auto"
                        >
                            {filtradas.map(marca => (
                                <Flex
                                    key={marca.id}
                                    px={4}
                                    py={3}
                                    align="center"
                                    gap={3}
                                    cursor="pointer"
                                    _hover={{ bg: 'surfaceHover' }}
                                    onClick={() => handleSelecionar(marca)}
                                    transition="background 0.1s"
                                    borderBottom="1px solid"
                                    borderColor="borderPrimary"
                                >
                                    {marca.logo ? (
                                        <Image
                                            src={marca.logo}
                                            alt={marca.nome}
                                            w="32px"
                                            h="32px"
                                            objectFit="contain"
                                            borderRadius="md"
                                            bg="white"
                                            p="2px"
                                        />
                                    ) : (
                                        <Avatar
                                            size="sm"
                                            name={marca.nome}
                                            bg="rgba(2,234,117,0.2)"
                                            color="secondary"
                                            fontWeight="900"
                                            fontSize="xs"
                                        />
                                    )}
                                    <VStack align="start" spacing={0} flex={1}>
                                        <Text fontSize="sm" fontWeight="700" color="textPrimary" isTruncated>
                                            {marca.nome}
                                        </Text>
                                        {marca.provedor && (
                                            <Text fontSize="10px" color="textMuted" isTruncated>
                                                {marca.provedor}
                                            </Text>
                                        )}
                                    </VStack>
                                    <Badge bg="rgba(2,234,117,0.15)" color="secondary" fontSize="9px" fontWeight="800" borderRadius="md">
                                        {countProdutos(marca)}/{PRODUTOS_NOGLE.length}
                                    </Badge>
                                </Flex>
                            ))}
                        </Box>
                    )}
                </Box>

                <Button
                    size="lg"
                    bg="secondary"
                    color="primary"
                    _hover={{ bg: 'secondaryHover' }}
                    fontWeight="800"
                    borderRadius="xl"
                    leftIcon={<AiOutlineSearch />}
                    onClick={() => {
                        if (filtradas.length > 0) {
                            handleSelecionar(filtradas[0]);
                        }
                    }}
                    isDisabled={!busca.trim() || loading}
                >
                    Pesquisar
                </Button>

                <Button
                    size="lg"
                    variant="outline"
                    borderColor="borderPrimary"
                    color="textSecondary"
                    _hover={{ bg: 'surfaceHover', color: 'textPrimary' }}
                    fontWeight="700"
                    borderRadius="xl"
                    onClick={() => {
                        setBusca('');
                        setMarcaSelecionada(null);
                    }}
                    isDisabled={!busca && !marcaSelecionada}
                >
                    Limpar
                </Button>

                <Button
                    size="lg"
                    variant="outline"
                    borderColor="borderPrimary"
                    color="secondary"
                    _hover={{ bg: 'rgba(2,234,117,0.1)' }}
                    fontWeight="700"
                    borderRadius="xl"
                    leftIcon={<AiOutlineSync />}
                    onClick={() => {
                        refetchMarcas();
                    }}
                    isLoading={loading}
                >
                    Atualizar
                </Button>
            </HStack>

            {/* ── Ficha da Marca ── */}
            {marcaSelecionada ? (
                <Box
                    bg="surface"
                    border="1px solid"
                    borderColor="borderPrimary"
                    borderRadius="2xl"
                    overflow="hidden"
                    position="relative"
                >
                    {/* Gradient header */}
                    <Box
                        h="120px"
                        bg="linear-gradient(135deg, rgba(2,234,117,0.1) 0%, rgba(66,153,225,0.08) 50%, rgba(159,122,234,0.06) 100%)"
                        position="relative"
                    >
                        <Box
                            position="absolute"
                            bottom={0}
                            left={0}
                            right={0}
                            h="60px"
                            bg="linear-gradient(to top, var(--chakra-colors-surface), transparent)"
                        />
                    </Box>

                    {/* Profile section */}
                    <Flex px={6} mt="-50px" position="relative" zIndex={2} direction="column">
                        <Flex align="end" gap={4}>
                            <Box
                                w="80px"
                                h="80px"
                                borderRadius="xl"
                                bg="white"
                                border="3px solid"
                                borderColor="borderPrimary"
                                overflow="hidden"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                            >
                                {marcaSelecionada.logo ? (
                                    <Image
                                        src={marcaSelecionada.logo}
                                        alt={marcaSelecionada.nome}
                                        w="100%"
                                        h="100%"
                                        objectFit="contain"
                                        p="6px"
                                    />
                                ) : (
                                    <Text fontSize="2xl" fontWeight="900" color="gray.400">
                                        {(marcaSelecionada.nome || '?')[0].toUpperCase()}
                                    </Text>
                                )}
                            </Box>
                            <VStack align="start" spacing={0} pb={1}>
                                <Text fontSize="xl" fontWeight="900" color="textPrimary">
                                    {marcaSelecionada.nome}
                                </Text>
                                {marcaSelecionada.provedor && (
                                    <Text fontSize="sm" color="textSecondary" fontWeight="600">
                                        Provedor: {marcaSelecionada.provedor}
                                    </Text>
                                )}
                            </VStack>
                        </Flex>

                        <Divider borderColor="borderPrimary" my={5} />

                        {/* Info grid */}
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} mb={5}>
                            <InfoItem icon={AiOutlineUser} label="Responsável" value={marcaSelecionada.responsavel_marca_nome} />
                            <InfoItem icon={AiOutlineEnvironment} label="Estado" value={marcaSelecionada.uf || marcaSelecionada.estado} />
                            <InfoItem icon={AiOutlinePhone} label="SAC" value={marcaSelecionada.sac} />
                            <InfoItem icon={AiOutlineGlobal} label="Site" value={marcaSelecionada.site} valueColor="#4299E1" />
                            <InfoItem icon={AiOutlineInfoCircle} label="WhatsApp" value={marcaSelecionada.whatsapp} />
                            <InfoItem icon={AiOutlineUser} label="Retenção" value={marcaSelecionada.responsavel_retencao_nome} />
                        </SimpleGrid>

                        {/* Produtos */}
                        <Box mb={5}>
                            <HStack spacing={2} mb={3}>
                                <Icon as={AiOutlineAppstore} color="secondary" boxSize="14px" />
                                <Text fontSize="10px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="2px">
                                    Produtos Nogle
                                </Text>
                                <Badge bg="rgba(2,234,117,0.15)" color="secondary" fontSize="9px" fontWeight="900" borderRadius="md">
                                    {countProdutos(marcaSelecionada)}/{PRODUTOS_NOGLE.length}
                                </Badge>
                            </HStack>
                            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
                                {PRODUTOS_NOGLE.map(prod => {
                                    const ativo = toBool(marcaSelecionada[prod.campo]);
                                    return (
                                        <Box
                                            key={prod.key}
                                            bg={ativo ? 'rgba(2,234,117,0.08)' : 'surfaceRaised'}
                                            border="1px solid"
                                            borderColor={ativo ? 'rgba(2,234,117,0.25)' : 'borderPrimary'}
                                            borderRadius="lg"
                                            px={3}
                                            py={3}
                                            textAlign="center"
                                            _hover={{ transform: 'translateY(-1px)' }}
                                            transition="all 0.15s"
                                        >
                                            <Icon
                                                as={prod.icon}
                                                color={ativo ? 'secondary' : 'textMuted'}
                                                boxSize="20px"
                                                mb={1}
                                            />
                                            <Text
                                                fontSize="10px"
                                                fontWeight="800"
                                                color={ativo ? 'textPrimary' : 'textMuted'}
                                                mb={1}
                                            >
                                                {prod.label}
                                            </Text>
                                            <Icon
                                                as={ativo ? AiOutlineCheckCircle : AiOutlineCloseCircle}
                                                color={ativo ? '#02EA75' : 'textMuted'}
                                                boxSize="14px"
                                            />
                                        </Box>
                                    );
                                })}
                            </SimpleGrid>
                        </Box>

                        {/* Observações */}
                        {marcaSelecionada.observacoes && (
                            <Box mb={5} bg="surfaceRaised" border="1px solid" borderColor="borderPrimary" borderRadius="lg" px={4} py={3}>
                                <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={1}>
                                    Observações
                                </Text>
                                <Text fontSize="sm" color="textSecondary" whiteSpace="pre-wrap">
                                    {marcaSelecionada.observacoes}
                                </Text>
                            </Box>
                        )}
                    </Flex>
                </Box>
            ) : (
                /* Empty state */
                !busca && (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        py={20}
                        bg="surface"
                        border="1px solid"
                        borderColor="borderPrimary"
                        borderRadius="2xl"
                        position="relative"
                        overflow="hidden"
                    >
                        <Box
                            position="absolute"
                            top="-50px"
                            right="-50px"
                            w="200px"
                            h="200px"
                            bg="radial-gradient(circle, rgba(2,234,117,0.06) 0%, transparent 70%)"
                            pointerEvents="none"
                        />
                        <Icon as={AiOutlineFileSearch} color="textMuted" boxSize="60px" mb={4} />
                        <Text color="textSecondary" fontWeight="700" fontSize="lg">
                            Pesquise uma marca acima
                        </Text>
                        <Text color="textMuted" fontSize="sm" mt={1}>
                            Comece a digitar o nome para ver os resultados
                        </Text>
                    </Flex>
                )
            )}
        </VStack>
    );
};

export default ConsultaMarca;
